/**
 * Per-line profit / gross-margin evaluation for quote part lines.
 *
 * Margin is gross margin: profit / sell (both ex-GST). The minimum acceptable
 * margin is tiered by the line's total cost ex-GST — cheaper lines must carry a
 * higher margin, big-ticket lines floor at 12%. A line whose actual margin
 * falls below its bracket's floor is flagged low so the tech sees thin lines at
 * a glance.
 *
 * Only `part` lines are checked against this scale (it is defined "at the part
 * level"). Labour / fixed-price lines have no separable cost (cost == sell), and
 * third-party lines are governed by their own snapshotted markup — callers there
 * use {@link evaluateMargin} for the profit/margin figures but ignore `isLow`.
 */

/**
 * Minimum gross margin keyed by total line cost ex-GST, as
 * `[inclusiveUpperBound, minMargin]` ordered ascending. The first bracket whose
 * upper bound the cost is ≤ wins; anything above the last bound floors at
 * {@link MARGIN_FLOOR_ABOVE}.
 */
const MARGIN_FLOORS: ReadonlyArray<readonly [maxCostExGst: number, minMargin: number]> = [
  [20, 0.5],
  [50, 0.4],
  [75, 0.3],
  [100, 0.2],
  [200, 0.15],
];

/** Floor applied to lines costing more than the last bracket (> $200). */
const MARGIN_FLOOR_ABOVE = 0.12;

/** Minimum acceptable gross margin for a part line of the given cost ex-GST. */
export const requiredMargin = (lineCostExGst: number): number => {
  for (const [maxCost, min] of MARGIN_FLOORS) {
    if (lineCostExGst <= maxCost) return min;
  }
  return MARGIN_FLOOR_ABOVE;
};

/**
 * Gross margin = (sell − cost) / sell. Returns null when there is no sell price
 * to take a margin against (sell ≤ 0), so callers render "—" rather than a
 * misleading 0% or a divide-by-zero.
 */
export const grossMargin = (
  costExGst: number,
  sellExGst: number,
): number | null => (sellExGst > 0 ? (sellExGst - costExGst) / sellExGst : null);

export interface MarginVerdict {
  /** Profit ex-GST = sell − cost. */
  profitExGst: number;
  /** Gross margin (profit / sell), or null when sell ≤ 0. */
  margin: number | null;
  /** The minimum margin floor for this line's cost bracket. */
  required: number;
  /**
   * True when the line has a real margin below its floor. Always false when
   * `margin` is null. Callers decide whether to surface this (part lines do;
   * third-party lines ignore it).
   */
  isLow: boolean;
}

/** Evaluate profit, margin, and the low-margin verdict for a single line. */
export const evaluateMargin = (
  costExGst: number,
  sellExGst: number,
): MarginVerdict => {
  const profitExGst = sellExGst - costExGst;
  const margin = grossMargin(costExGst, sellExGst);
  const required = requiredMargin(costExGst);
  return {
    profitExGst,
    margin,
    required,
    isLow: margin !== null && margin < required,
  };
};
